import { LoaderCircle, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { PhoneInput } from "../../components/ui/phone-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { authClient } from "../../lib/auth-client";
import { getFriendlyErrorMessage, loginCopy as t } from "../../lib/login-copy";
import { breadcrumbOtpSend, reportOtpSendError } from "../../lib/otp-telemetry";
import { usePlatformAuth } from "../../lib/use-platform-auth";

const logoLight = "/branding/logo-light.svg";

export function LoginPage() {
  const { sessionData, isPending, bootstrapLoading } = usePlatformAuth();
  const [signInMode, setSignInMode] = useState<"email" | "phone">("email");
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const emailOtpContainerRef = useRef<HTMLDivElement | null>(null);
  const lastAttemptedEmailOtp = useRef<string | null>(null);
  const [phone, setPhone] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const phoneOtpContainerRef = useRef<HTMLDivElement | null>(null);
  const lastAttemptedPhoneOtp = useRef<string | null>(null);

  const resetEmailFlow = () => {
    setEmailOtp("");
    setEmailOtpSent(false);
    setError("");
    lastAttemptedEmailOtp.current = null;
  };

  const resetPhoneFlow = () => {
    setPhoneOtp("");
    setPhoneOtpSent(false);
    setError("");
    lastAttemptedPhoneOtp.current = null;
  };

  const handlePasskeySignIn = async () => {
    setIsPasskeyLoading(true);
    setError("");
    try {
      const { error } = await authClient.signIn.passkey();
      if (error) setError(getFriendlyErrorMessage(error.message, t));
    } catch (err) {
      setError(getFriendlyErrorMessage(err instanceof Error ? err.message : "", t));
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const sendEmailOtp = async () => {
    setIsEmailLoading(true);
    setError("");
    if (!email) {
      setError(t.errors.emailRequired);
      setIsEmailLoading(false);
      return;
    }
    try {
      const { error: otpError } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      if (otpError) setError(getFriendlyErrorMessage(otpError.message, t));
      else setEmailOtpSent(true);
    } catch {
      setError(t.errors.generic);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const verifyEmailOtp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsEmailLoading(true);
    setError("");
    if (emailOtp.length !== 6) {
      setError(t.errors.invalidOtp);
      setIsEmailLoading(false);
      return;
    }
    try {
      const res = await authClient.signIn.emailOtp({ email, otp: emailOtp });
      if (res.error) setError(getFriendlyErrorMessage(res.error.message, t));
    } catch (err) {
      setError(getFriendlyErrorMessage(err instanceof Error ? err.message : "", t));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const submitEmailOtp = async (otpCode: string) => {
    setIsEmailLoading(true);
    setError("");
    if (otpCode.length !== 6) {
      setError(t.errors.invalidOtp);
      setIsEmailLoading(false);
      return;
    }
    try {
      const res = await authClient.signIn.emailOtp({ email, otp: otpCode });
      if (res.error) setError(getFriendlyErrorMessage(res.error.message, t));
    } catch (err) {
      setError(getFriendlyErrorMessage(err instanceof Error ? err.message : "", t));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const sendPhoneOtp = async () => {
    setIsPhoneLoading(true);
    setError("");
    const normalizedPhone = phone.trim();
    if (!normalizedPhone) {
      setError(t.errors.phoneRequired);
      setIsPhoneLoading(false);
      return;
    }
    if (!isPossiblePhoneNumber(normalizedPhone)) {
      setError(t.errors.invalidPhone);
      setIsPhoneLoading(false);
      return;
    }
    const otpContext = { screen: "login" as const, isResend: phoneOtpSent, phone: normalizedPhone };
    try {
      breadcrumbOtpSend(otpContext);
      const { error: otpError } = await authClient.phoneNumber.sendOtp({
        phoneNumber: normalizedPhone,
      });
      if (otpError) {
        reportOtpSendError(otpError, otpContext);
        setError(getFriendlyErrorMessage(otpError.message, t));
      } else setPhoneOtpSent(true);
    } catch (err) {
      reportOtpSendError(err, otpContext);
      setError(getFriendlyErrorMessage(err instanceof Error ? err.message : "", t));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const submitPhoneOtp = async (otpCode: string) => {
    setIsPhoneLoading(true);
    setError("");
    if (otpCode.length !== 6) {
      setError(t.errors.invalidOtp);
      setIsPhoneLoading(false);
      return;
    }
    try {
      const res = await (authClient.phoneNumber as any).verify({
        phoneNumber: phone.trim(),
        code: otpCode,
      });
      if (res.error) setError(getFriendlyErrorMessage(res.error.message, t));
    } catch (err) {
      setError(getFriendlyErrorMessage(err instanceof Error ? err.message : "", t));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const verifyPhoneOtp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitPhoneOtp(phoneOtp);
  };

  useEffect(() => {
    if (!emailOtpSent) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      emailOtpContainerRef.current?.querySelector("input")?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [emailOtpSent]);

  useEffect(() => {
    if (!emailOtpSent || isEmailLoading || emailOtp.length !== 6) {
      return;
    }
    if (lastAttemptedEmailOtp.current === emailOtp) {
      return;
    }
    lastAttemptedEmailOtp.current = emailOtp;
    void submitEmailOtp(emailOtp);
  }, [emailOtp, emailOtpSent, isEmailLoading]);

  useEffect(() => {
    if (!phoneOtpSent) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      phoneOtpContainerRef.current?.querySelector("input")?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [phoneOtpSent]);

  useEffect(() => {
    if (!phoneOtpSent || isPhoneLoading || phoneOtp.length !== 6) {
      return;
    }
    if (lastAttemptedPhoneOtp.current === phoneOtp) {
      return;
    }
    lastAttemptedPhoneOtp.current = phoneOtp;
    void submitPhoneOtp(phoneOtp);
  }, [phoneOtp, phoneOtpSent, isPhoneLoading]);

  if (isPending || (sessionData?.user && bootstrapLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (sessionData?.user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background lg:grid lg:grid-cols-2">
      <div className="pointer-events-none absolute left-0 top-0 -z-10 h-full w-full overflow-hidden">
        <div className="absolute left-[-10%] top-[-10%] h-[40%] w-[40%] rounded-full bg-accent/12 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[40%] h-[30%] w-[30%] rounded-full bg-primary/8 blur-[100px]" />
      </div>

      <div className="relative z-10 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2">
            <img src={logoLight} alt="Forhandlerbørsen" className="h-14 w-auto" />
          </div>

          <div className="mb-8">
            <h1 className="mb-2 text-4xl font-black tracking-tight text-primary">{t.title}</h1>
            <p className="text-base text-muted-foreground">{t.subtitle}</p>
          </div>

          <div className="glass relative overflow-hidden rounded-[2rem] border border-white/50 p-6 sm:p-8">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
            <div className="relative z-10 flex flex-col gap-6">
              {error ? <ErrorAlert message={error} /> : null}

              <Button className="h-auto w-full py-6 text-base font-bold transition-all hover:-translate-y-1" onClick={handlePasskeySignIn} disabled={isPasskeyLoading}>
                {isPasskeyLoading ? <LoaderCircle className="mr-2 h-5 w-5 animate-spin" /> : <PasskeyIcon />}
                {t.passkeyButton}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-secondary px-3 font-medium text-muted-foreground">{t.divider}</span>
                </div>
              </div>

              <Tabs value={signInMode} onValueChange={(value) => {
                setSignInMode(value as "email" | "phone");
                setError("");
                resetPhoneFlow();
                resetEmailFlow();
              }} className="w-full">
                <TabsList className="mb-6 grid w-full grid-cols-2 rounded-xl bg-muted p-1">
                  <TabsTrigger value="email" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">{t.emailTab}</TabsTrigger>
                  <TabsTrigger value="phone" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">{t.smsTab}</TabsTrigger>
                </TabsList>

                <TabsContent value="email" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                  <form onSubmit={emailOtpSent ? verifyEmailOtp : (event) => {
                    event.preventDefault();
                    void sendEmailOtp();
                  }} className="flex flex-col gap-4">
                    <FieldLabel htmlFor="email">{t.emailLabel}</FieldLabel>
                    <Input id="email" type="email" placeholder={t.emailPlaceholder} value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" disabled={emailOtpSent} required className="h-11 rounded-xl transition-colors focus:bg-white" />
                    {emailOtpSent ? (
                      <>
                        <FieldLabel htmlFor="emailOtp">{t.otpLabel}</FieldLabel>
                        <div className="space-y-3">
                          <div
                            ref={emailOtpContainerRef}
                            className="rounded-2xl border border-border/80 bg-white/90 p-3 shadow-sm"
                          >
                            <InputOTP
                              id="emailOtp"
                              maxLength={6}
                              value={emailOtp}
                              onChange={setEmailOtp}
                              autoFocus
                              autoComplete="one-time-code"
                              inputMode="numeric"
                              containerClassName="w-full justify-between gap-2"
                            >
                              <InputOTPGroup className="w-full justify-between gap-2">
                                {Array.from({ length: 6 }).map((_, index) => (
                                  <InputOTPSlot
                                    key={index}
                                    index={index}
                                    className="h-12 w-12 rounded-xl border border-input bg-background text-base font-semibold first:rounded-xl first:border-l last:rounded-xl"
                                  />
                                ))}
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <p className="text-muted-foreground">
                              Skriv inn koden vi sendte til e-postadressen din.
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setEmailOtp("");
                                void sendEmailOtp();
                              }}
                              className="font-semibold text-primary transition hover:text-accent"
                              disabled={isEmailLoading}
                            >
                              {t.resendCode}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : null}
                    <Button
                      type="submit"
                      className="mt-2 h-11 w-full rounded-xl"
                      disabled={isEmailLoading || (emailOtpSent && emailOtp.length !== 6)}
                    >
                      {isEmailLoading ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{emailOtpSent ? t.verifying : t.sending}</> : emailOtpSent ? t.submitSignIn : t.submitSend}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="phone" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                  <form onSubmit={phoneOtpSent ? verifyPhoneOtp : (event) => {
                    event.preventDefault();
                    void sendPhoneOtp();
                  }} className="flex flex-col gap-4">
                    <FieldLabel htmlFor="phone">{t.phoneLabel}</FieldLabel>
                    <PhoneInput
                      id="phone"
                      placeholder={t.phonePlaceholder}
                      value={phone as any}
                      onChange={(value: string | undefined) => setPhone(value ?? "")}
                      disabled={phoneOtpSent}
                      defaultCountry="NO"
                      international={false}
                      countryCallingCodeEditable={false}
                      className="h-11 shadow-sm transition-all focus-within:ring-2 focus-within:ring-primary/20"
                    />
                    {phoneOtpSent ? (
                      <>
                        <FieldLabel htmlFor="phoneOtp">{t.otpSmsLabel}</FieldLabel>
                        <div className="space-y-3">
                          <div
                            ref={phoneOtpContainerRef}
                            className="rounded-2xl border border-border/80 bg-white/90 p-3 shadow-sm"
                          >
                            <InputOTP
                              id="phoneOtp"
                              maxLength={6}
                              value={phoneOtp}
                              onChange={setPhoneOtp}
                              autoFocus
                              autoComplete="one-time-code"
                              inputMode="numeric"
                              containerClassName="w-full justify-between gap-2"
                            >
                              <InputOTPGroup className="w-full justify-between gap-2">
                                {Array.from({ length: 6 }).map((_, index) => (
                                  <InputOTPSlot
                                    key={index}
                                    index={index}
                                    className="h-12 w-12 rounded-xl border border-input bg-background text-base font-semibold first:rounded-xl first:border-l last:rounded-xl"
                                  />
                                ))}
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <p className="text-muted-foreground">
                              Skriv inn koden vi sendte til nummeret ditt.
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setPhoneOtp("");
                                void sendPhoneOtp();
                              }}
                              className="font-semibold text-primary transition hover:text-accent"
                              disabled={isPhoneLoading}
                            >
                              {t.resendCode}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : null}
                    <Button
                      type="submit"
                      className="mt-2 h-11 w-full rounded-xl"
                      disabled={isPhoneLoading || (phoneOtpSent && phoneOtp.length !== 6)}
                    >
                      {isPhoneLoading ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{phoneOtpSent ? t.verifying : t.sending}</> : phoneOtpSent ? t.submitSignIn : t.submitSend}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <a href="https://forhandlerborsen.no/registrer" target="_blank" rel="noreferrer" className="mt-5 block text-center text-sm font-semibold text-primary/70 transition hover:text-accent">
            Ny hos Forhandlerbørsen? Opprett forhandlerkonto
          </a>
        </div>
      </div>

      <div className="relative hidden items-center justify-center overflow-hidden border-l border-white/10 bg-primary p-12 lg:flex">
        <div className="absolute inset-0 bg-primary" />
        <div className="absolute right-[-10%] top-[-10%] h-[70%] w-[70%] animate-pulse rounded-full bg-accent/35 opacity-70 blur-[100px] mix-blend-screen" style={{ animationDuration: "8s" }} />
        <div className="absolute bottom-[-10%] left-[-10%] h-[60%] w-[60%] animate-pulse rounded-full bg-white/10 opacity-70 blur-[120px] mix-blend-screen" style={{ animationDuration: "12s", animationDelay: "2s" }} />
        <div className="absolute left-[20%] top-[20%] h-[40%] w-[40%] animate-pulse rounded-full bg-accent/20 opacity-60 blur-[80px] mix-blend-screen" style={{ animationDuration: "10s", animationDelay: "4s" }} />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:32px_32px] opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_30%,transparent_100%)]" />

        <div className="relative z-10 w-full max-w-lg space-y-6 text-foreground">
          <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm font-semibold text-white/80 backdrop-blur-xl">
            <span className="mr-2 flex h-2 w-2 rounded-full bg-accent" />
            Forhandlerbørsen
          </div>
          <h2 className="text-balance text-5xl font-black tracking-tight text-white">{t.footerTitle}</h2>
          <p className="text-balance text-xl font-medium text-white/60">{t.footerSubtitle}</p>
          <div className="flex gap-4 pt-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`flex h-10 w-10 items-center justify-center rounded-full border-2 border-background text-xs font-medium ${i === 1 ? "bg-accent text-white" : i === 2 ? "bg-white/90 text-primary" : i === 3 ? "bg-[#0f766e] text-white" : "bg-white/10 text-white/40"}`}>
                  {i === 4 ? <Users size={14} /> : String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-sm font-semibold text-white">{t.usersCount}</span>
              <span className="text-xs text-white/50">{t.trustPlatform}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel(props: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label {...props} className={`text-sm font-medium text-foreground/80 ${props.className ?? ""}`} />;
}

function ErrorAlert({ message }: { message: string }) {
  return <div className="rounded-2xl border border-destructive/15 bg-destructive/8 px-4 py-3 text-sm leading-6 text-destructive">{message}</div>;
}

function PasskeyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-5 w-5">
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </svg>
  );
}
